import { forOwn } from "lodash";
import axios, { CancelToken } from "axios";
import { getAPIBaseURL, SUBCONTRACTING_FOR_USER, TOKEN_KEY } from "@/env";
import JSZip from "jszip";

const API_SIGN_URL = "/s3_file_attachment";
const API_S3_SIGNED_URL_MEDIA_UPLOAD = "/s3_signed_url_media_upload";
const API_S3_SIGNED_URL_DELIVERY = "/s3_signed_url_delivery";
const API_S3_SIGNED_URL_PROJECT = "/s3_signed_url_project";
const API_UPLOAD_PROFILE_PICTURE = "/upload_profile_picture";
const API_DELETE_PROFILE_PICTURE = "/remove_profile_picture";
const API_S3_SIGNED_URL_USER_DELIVERY = "/s3_signed_url_user_delivery";

interface FileAttachment {
  id: string;
}

interface Fields {
  [key: string]: string;
}

export interface SignedRequestResponse {
  fields: Fields;
  url: string;
  headers?: Headers;
  file: File;
  fileAttachment: FileAttachment;
}

export interface S3SignedRequestByIdResponse {
  upload_id: string;
  urls: string[];
  key: string;
}

export function getSignedRequest(
  projectId: string,
  file: File,
): Promise<SignedRequestResponse> {
  const token = localStorage.getItem(TOKEN_KEY);

  return axios
    .post(
      `${getAPIBaseURL()}${API_SIGN_URL}`,
      new URLSearchParams({
        file_name: file.name,
        file_type: file.type,
        graphene_id: projectId,
      }),
      {
        headers: {
          "content-type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
          "x-subcontracting-for-user": localStorage.getItem(
            SUBCONTRACTING_FOR_USER,
          ),
        },
      },
    )
    .then((result) => {
      const resp = result.data.data;
      if (resp.success) {
        const fields: Fields = {};
        forOwn(resp.response.signature.fields, (_value, key) => {
          fields[key] = resp.response.signature.fields[key];
        });
        return {
          fields,
          url: resp.response.signature.url,
          file,
          fileAttachment: resp.fileAttachment,
        };
      }
      throw new Error("Something went wrong while generating a signed URL!");
    });
}

export function getSignedURLsByDeliveryId(
  deliveryId: string,
  file: File,
  cancelToken?: CancelToken,
): Promise<S3SignedRequestByIdResponse> {
  const token = localStorage.getItem(TOKEN_KEY);

  return axios
    .post(
      `${getAPIBaseURL()}${API_S3_SIGNED_URL_DELIVERY}`,
      {
        file: {
          name: file.name,
          size: file.size,
        },
        delivery_id: deliveryId,
      },
      {
        cancelToken,
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-subcontracting-for-user": localStorage.getItem(
            SUBCONTRACTING_FOR_USER,
          ),
        },
      },
    )
    .then((result) => {
      const resp = result.data.data;
      if (resp.success) {
        return resp.response;
      }
      throw new Error("Something went wrong while generating a signed URL's!");
    })
    .catch((error) => {
      throw new Error(
        `Something went wrong while generating a signed URL's! ${error}`,
      );
    });
}

export function getSignedURLsByUploadId(
  uploadId: string,
  file: File,
  cancelToken?: CancelToken,
): Promise<S3SignedRequestByIdResponse> {
  const token = localStorage.getItem(TOKEN_KEY);

  return axios
    .post(
      `${getAPIBaseURL()}${API_S3_SIGNED_URL_MEDIA_UPLOAD}`,
      {
        file: {
          name: file.name,
          size: file.size,
        },
        upload_id: uploadId,
      },
      {
        cancelToken,
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-subcontracting-for-user": localStorage.getItem(
            SUBCONTRACTING_FOR_USER,
          ),
        },
      },
    )
    .then((result) => {
      const resp = result.data.data;
      if (resp.success) {
        return resp.response;
      }
      throw new Error("Something went wrong while generating a signed URL's!");
    })
    .catch((error) => {
      throw new Error(
        `Something went wrong while generating a signed URL's! ${error}`,
      );
    });
}

export function getSignedURLsForUserDelivery(
  projectId: string,
  file: File,
  cancelToken?: CancelToken,
): Promise<S3SignedRequestByIdResponse> {
  const token = localStorage.getItem(TOKEN_KEY);

  return axios
    .post(
      `${getAPIBaseURL()}${API_S3_SIGNED_URL_USER_DELIVERY}`,
      {
        file: {
          name: file.name,
          size: file.size,
        },
        project_id: projectId,
      },
      {
        cancelToken,
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-subcontracting-for-user": localStorage.getItem(
            SUBCONTRACTING_FOR_USER,
          ),
        },
      },
    )
    .then((result) => {
      const resp = result.data.data;
      if (resp.success) {
        return resp.response;
      }
      throw new Error("Something went wrong while generating a signed URL's!");
    })
    .catch((error) => {
      throw new Error(
        `Something went wrong while generating a signed URL's! ${error}`,
      );
    });
}

export function getSignedURLsByProjectId(
  projectId: string,
  file: File,
  cancelToken?: CancelToken,
): Promise<S3SignedRequestByIdResponse> {
  const token = localStorage.getItem(TOKEN_KEY);

  console.log(
    "Getting signed URLs for:",
    file.name,
    "Size:",
    file.size,
    "Project:",
    projectId,
  );

  return axios
    .post(
      `${getAPIBaseURL()}${API_S3_SIGNED_URL_PROJECT}`,
      {
        file: {
          name: file.name,
          size: file.size,
        },
        project_id: projectId,
      },
      {
        cancelToken,
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    )
    .then((result) => {
      const resp = result.data.data;
      if (resp.success) {
        return resp.response;
      }
      throw new Error("Something went wrong while generating a signed URL's!");
    })
    .catch((error) => {
      console.error("Signed URL error:", error);
      throw error;
    });
}

export function getETA(startTime: Date, loaded: number, total: number) {
  const timeElapsed: number =
    (new Date().getTime() - startTime.getTime()) / 1000;
  const uploadSpeed = loaded / timeElapsed;
  const timeLeft = (total - loaded) / uploadSpeed;

  const hours = Math.floor(timeLeft / 60 / 60);
  const minutes = Math.floor(timeLeft / 60) - hours * 60;
  const seconds = Math.floor(timeLeft % 60);

  return `${hours}h ${minutes}m ${seconds}s`;
}

export function getUploadInfo(loaded: number, total: number) {
  const ONE_MB = 1024 * 1024;
  const ONE_GB = ONE_MB * 1024;
  const divisor = total > ONE_GB ? ONE_GB : ONE_MB;
  const unit = divisor === ONE_GB ? "GB" : "MB";

  const uploaded = (loaded / divisor).toFixed(2);
  const totalSize = (total / divisor).toFixed(2);

  return `${uploaded} ${unit} of ${totalSize} ${unit}`;
}

export interface Directory {
  name: string;
  children?: Directory[];
  absolutePath: string;
  type: "directory" | "file";
}

export function createDirectoryStructure(fileNames: string[]): Directory[] {
  const tree: Directory = {
    name: "root",
    children: [],
    type: "directory",
    absolutePath: "",
  };

  function createTree(
    subtree: Directory,
    filePath: string[],
    currentPath: string[],
  ) {
    const name: string = filePath.shift()!;
    currentPath.push(name);
    if (filePath.length === 0) {
      subtree.children!.push({
        name,
        absolutePath: currentPath.join("/"),
        type: "file",
      });
      return;
    }
    let currentDir = subtree.children!.filter(
      (entry) => entry.name === name,
    )[0];
    if (!currentDir) {
      currentDir = {
        name,
        children: [],
        absolutePath: currentPath.join("/"),
        type: "directory",
      };
      subtree.children!.push(currentDir);
    }
    createTree(currentDir, filePath, currentPath);
    currentPath.pop();
  }

  fileNames.forEach((file) => {
    createTree(tree, file.split("/"), []);
  });
  return tree.children!;
}

export function findAllFileNames(directory: Directory): string[] {
  const filesNames: string[] = [];

  function digDirectory(currentElement: Directory) {
    if (currentElement.type === "file") {
      filesNames.push(currentElement.absolutePath);
    }
    if (currentElement.children)
      currentElement.children.forEach((dir) => digDirectory(dir));
  }

  digDirectory(directory);

  return filesNames;
}

export const bytesToGB = (bytes: number) => {
  return (bytes / 1024 ** 3).toFixed(1);
};

export const isZipFile = (filename: string) =>
  filename.split(".").pop() === "zip";

export async function validateUpload(files: File[]) {
  let isValid = true;
  let suspect: string | null = null;

  for (const file of files) {
    if (!isValid) break;

    if (!(file.size > 0)) {
      isValid = false;
      suspect = file.name;
      break;
    }

    if (file.name.endsWith(".zip")) {
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(file);

      const directories = new Set<string>();
      const fileNames = new Set<string>();

      for (const zipObj of Object.values(zipContent.files).filter(
        (f) => !f.name.includes("__MACOSX") && !f.name.includes("DS_Store"),
      )) {
        if (zipObj.dir) {
          directories.add(zipObj.name);
        } else {
          fileNames.add(zipObj.name);

          const content = await zipObj.async("uint8array");
          // Checks if the file is empty
          if (content.length === 0) {
            isValid = false;
            suspect = zipObj.name;
            break;
          }
        }
      }

      // Checks if any folder is empty
      for (const dir of directories) {
        const hasFilesInFolder = Array.from(fileNames).some((fileName) =>
          fileName.startsWith(dir),
        );

        if (!hasFilesInFolder) {
          isValid = false;
          suspect = dir;
          break;
        }
      }
    }
  }

  return { isValid, suspect };
}

export function uploadUserProfilePicture(file: File) {
  const token = localStorage.getItem(TOKEN_KEY);
  const formData = new FormData();
  formData.append("file", file, file.name);
  return axios
    .post(`${getAPIBaseURL()}${API_UPLOAD_PROFILE_PICTURE}`, formData, {
      withCredentials: false,
      headers: {
        Authorization: `Bearer ${token}`,
        "x-subcontracting-for-user": localStorage.getItem(
          SUBCONTRACTING_FOR_USER,
        ),
      },
    })
    .then((result) => {
      const resp = result.data.data;
      if (!resp.success) {
        const errorMessage = resp.errors?.__all__[0]?.toString();
        throw new Error(
          errorMessage ||
            "Something went wrong while uploading profile picture",
        );
      }
    });
}

export function removeUserProfilePicture() {
  const token = localStorage.getItem(TOKEN_KEY);
  return axios
    .delete(`${getAPIBaseURL()}${API_DELETE_PROFILE_PICTURE}`, {
      withCredentials: false,
      headers: {
        Authorization: `Bearer ${token}`,
        "x-subcontracting-for-user": localStorage.getItem(
          SUBCONTRACTING_FOR_USER,
        ),
      },
    })
    .then((result) => {
      const resp = result.data.data;
      if (!resp.success) {
        const errorMessage = resp.errors?.__all__[0]?.toString();
        throw new Error(
          errorMessage ||
            "Something went wrong while deleting profile picture.",
        );
      }
    });
}
